import { uploadFile } from '@/lib/upload';
import { videoPosterUrl } from '@flowmuse/api-core/media-shared';
import { useAuth, useUser } from '@clerk/nextjs';
import { useNodeOperations } from '@/providers/node-operations';
import { useProject } from '@/providers/project';
import { useReactFlow } from '@xyflow/react';
import { toast } from 'sonner';
import { nanoid } from 'nanoid';
import i18next from '@/lib/i18n';

const t = (k: string, opts?: any): string => String(i18next.t(k, opts));

export type ExtractMode = 'first' | 'last';

/** 等待 video 元素某个事件（或 error / 10s 超时） */
const waitVideoEvent = (
  video: HTMLVideoElement,
  event: 'loadedmetadata' | 'seeked',
  errorMessage: string,
  timeoutMessage: string,
): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onOk = () => { cleanup(); resolve(); };
    const onError = () => { cleanup(); reject(new Error(errorMessage)); };
    const cleanup = () => {
      if (timer) clearTimeout(timer);
      try { video.removeEventListener(event, onOk); } catch { }
      try { video.removeEventListener('error', onError); } catch { }
    };
    video.addEventListener(event, onOk, { once: true });
    video.addEventListener('error', onError, { once: true });
    timer = setTimeout(() => { cleanup(); reject(new Error(timeoutMessage)); }, 10000);
  });

export type VideoCaptureSession = {
  /** 源视频像素宽高 */
  width: number;
  height: number;
  /** 时长（秒），元数据不可用时为 0 */
  duration: number;
  /** 截取指定时间点画面（自动 clamp 到片长内），返回 PNG dataURL */
  captureAt: (timeSec: number) => Promise<string>;
  /** 释放视频元素与 blob URL；之后不可再 captureAt */
  dispose: () => void;
};

/**
 * 打开视频截帧会话：视频只加载一次（跨域源经 /api/proxy 代理），
 * 之后可反复 seek + 截帧，避免逐帧重复下载整个视频。用完必须 dispose。
 */
export const openVideoForCapture = async (videoUrl: string): Promise<VideoCaptureSession> => {
  const video = document.createElement('video');
  video.muted = true;
  video.preload = 'auto';
  let objectUrl: string | undefined;

  const dispose = () => {
    try { if (objectUrl) URL.revokeObjectURL(objectUrl); } catch { }
    try { video.removeAttribute('src'); video.load(); } catch { }
  };

  try {
    let src = String(videoUrl);
    try {
      const loc = window.location;
      const isSameOrigin = new URL(src, loc.origin).origin === loc.origin;
      if (!isSameOrigin) {
        const resp = await fetch('/api/proxy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: src }),
          cache: 'no-store',
        });
        if (!resp.ok) {
          let err = 'Proxy request failed';
          try { const j = await resp.json(); err = (j as any)?.error || err; } catch { }
          if (/Host not allowed/i.test(err)) {
            try { const h = new URL(src).hostname; toast.error(t('Proxy blocked host: {{host}}. Please add the host to PROXY_ALLOWED_HOSTS', { host: h })); } catch { toast.error(t('Proxy blocked host. Please configure PROXY_ALLOWED_HOSTS')); }
          }
          throw new Error(err);
        }
        const blob = await resp.blob();
        objectUrl = URL.createObjectURL(blob);
        src = objectUrl;
      }
    } catch {
      // 忽略代理失败，尝试直接加载（需远端允许 CORS）
    }

    video.src = src;

    if (video.readyState < 1) {
      await waitVideoEvent(
        video,
        'loadedmetadata',
        'Failed to load video metadata',
        'Timeout loading video metadata',
      );
    }

    const duration = Number.isFinite(video.duration) ? video.duration : 0;

    const captureAt = async (timeSec: number): Promise<string> => {
      const safeTime = duration > 0
        ? Math.max(0, Math.min(duration - 0.001, timeSec))
        : Math.max(0, timeSec);
      video.currentTime = safeTime;
      await waitVideoEvent(video, 'seeked', 'Failed to seek video', 'Timeout seeking video');

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 1280;
      canvas.height = video.videoHeight || 720;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas not supported');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL('image/png');
    };

    return {
      width: video.videoWidth || 1280,
      height: video.videoHeight || 720,
      duration,
      captureAt,
      dispose,
    };
  } catch (e) {
    dispose();
    throw e;
  }
};

/**
 * 从视频 URL 提取一帧（首帧/尾帧），返回 dataURL 与尺寸。
 * 内部自动处理跨域源通过 /api/proxy 代理的情况。
 */
export const extractVideoFrame = async (
  videoUrl: string,
  mode: ExtractMode
): Promise<{ dataUrl: string; width: number; height: number }> => {
  const session = await openVideoForCapture(videoUrl);
  try {
    const time = mode === 'first' ? 0 : Math.max(0, session.duration - 0.05);
    const dataUrl = await session.captureAt(time);
    return { dataUrl, width: session.width, height: session.height };
  } finally {
    session.dispose();
  }
};

/**
 * 按指定秒数提取一帧
 */
export const extractVideoFrameAt = async (
  videoUrl: string,
  timeSec: number
): Promise<{ dataUrl: string; width: number; height: number }> => {
  const session = await openVideoForCapture(videoUrl);
  try {
    const dataUrl = await session.captureAt(timeSec);
    return { dataUrl, width: session.width, height: session.height };
  } finally {
    session.dispose();
  }
};

/**
 * 将 dataURL 上传到 Supabase 并返回公开 URL 和类型
 */
export const uploadImageDataUrl = async (
  dataUrl: string,
  token: string,
  userId: string,
  filename: string,
  projectId?: string
): Promise<{ url: string; type: string }> => {
  const blob = await (await fetch(dataUrl)).blob();
  const file = new File([blob], filename, { type: 'image/png' });
  return uploadFile(file, 'files', { token, userId, projectId: projectId ? String(projectId) : undefined, assetType: 'image' });
};

/**
 * Hook：从视频中提取一帧并在画布右侧新建 image 节点（不连线）
 */
export const useExtractFrame = () => {
  const { addNode } = useNodeOperations();
  const { getToken } = useAuth();
  const { user } = useUser();
  const project = useProject();
  const { getNode, updateNode, addEdges } = useReactFlow();

  // 尝试基于 Supabase 公网视频 URL 推导已生成的首/尾帧 URL（<base>_first.jpg / <base>_last.jpg）

  // 以 <img> 方式探测图片是否存在，并读取尺寸
  const probeImage = (imgUrl: string): Promise<{ width: number; height: number } | null> => {
    return new Promise((resolve) => {
      try {
        const img = new Image();
        // 不设置 crossOrigin，尽量避免 CORS 限制（我们不读像素，仅读 naturalWidth/Height）
        img.onload = () => resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
        img.onerror = () => resolve(null);
        img.src = imgUrl + (imgUrl.includes('?') ? `&t=${Date.now()}` : `?t=${Date.now()}`); // 防缓存
      } catch {
        resolve(null);
      }
    });
  };

  const extractToImageNode = async (
    nodeId: string,
    mode: ExtractMode,
    videoUrl: string
  ) => {
    try {
      // 1) 优先尝试使用已生成的首/尾帧（与服务端生成规则保持一致：<videoBase>_first.jpg / <videoBase>_last.jpg）
      const candidatePosterUrl = videoPosterUrl(videoUrl, mode);
      if (candidatePosterUrl) {
        const probed = await probeImage(candidatePosterUrl);
        if (probed && probed.width > 0 && probed.height > 0) {
          const current = getNode(nodeId);
          const baseX = current?.position.x ?? 0;
          const baseY = current?.position.y ?? 0;
          const xOffset = (current?.measured?.width ?? 300) + 80;
          const yOffset = ((current?.measured?.height ?? 240) / 2) + 40;
          const posY = mode === 'first' ? (baseY - yOffset) : mode === 'last' ? (baseY + yOffset) : baseY;
          const newId = addNode('image', {
            data: {
              content: { url: candidatePosterUrl, type: 'image/jpeg' },
              generated: { url: candidatePosterUrl, type: 'image/jpeg' },
              sourceUrl: candidatePosterUrl,
              width: probed.width,
              height: probed.height,
              size: `${probed.width}x${probed.height}`,
            },
            position: { x: baseX + xOffset, y: posY },
          });
          try {
            addEdges?.([
              { id: nanoid(), source: nodeId, target: newId, type: 'animated' as const },
            ]);
          } catch { }
          try {
            updateNode(newId, { selected: true });
          } catch { }
          toast.success(t('Using pre-generated frame'));
          return;
        }
      }

      // 2) 兜底：从视频中提取并上传
      const { dataUrl, width, height } = await extractVideoFrame(videoUrl, mode);

      const token = await getToken({ template: 'supabase' });
      if (!token) {
        toast.error(t('Please sign in to upload frame'));
        return;
      }
      if (!user?.id) {
        toast.error(t('User not found'));
        return;
      }

      toast.message(t('Uploading frame…'));
      const filename = `${mode}-frame-${Date.now()}.png`;
      const { url: uploadedUrl, type } = await uploadImageDataUrl(
        dataUrl,
        token,
        user.id,
        filename,
        String(project?.id || '')
      );

      const current = getNode(nodeId);
      const baseX = current?.position.x ?? 0;
      const baseY = current?.position.y ?? 0;
      // 位置：首帧右上，尾帧右下
      const xOffset = (current?.measured?.width ?? 300) + 80;
      const yOffset = ((current?.measured?.height ?? 240) / 2) + 40;
      const posY = mode === 'first' ? (baseY - yOffset) : mode === 'last' ? (baseY + yOffset) : baseY;
      const newId = addNode('image', {
        data: {
          content: { url: uploadedUrl, type },
          generated: { url: uploadedUrl, type },
          sourceUrl: uploadedUrl,
          width,
          height,
          size: `${width}x${height}`,
        },
        position: { x: baseX + xOffset, y: posY },
      });
      try {
        addEdges?.([
          { id: nanoid(), source: nodeId, target: newId, type: 'animated' as const },
        ]);
      } catch { }
      try {
        updateNode(newId, { selected: true });
      } catch { }

      toast.success(t('Frame extracted to new image node'));
    } catch (e) {
      console.error(e);
      toast.error(t('Failed to extract frame'));
    }
  };

  const extractToImageNodeAt = async (
    nodeId: string,
    timeSec: number,
    videoUrl: string
  ) => {
    try {
      const { dataUrl, width, height } = await extractVideoFrameAt(videoUrl, timeSec);

      const token = await getToken({ template: 'supabase' });
      if (!token) { toast.error(t('Please sign in to upload frame')); return; }
      if (!user?.id) { toast.error(t('User not found')); return; }

      toast.message(t('Uploading frame…'));
      const filename = `frame-${Math.round(timeSec * 1000)}ms-${Date.now()}.png`;
      const { url: uploadedUrl, type } = await uploadImageDataUrl(dataUrl, token, user.id, filename, String(project?.id || ''));

      const current = getNode(nodeId);
      const baseX = current?.position.x ?? 0;
      const baseY = current?.position.y ?? 0;
      const newId = addNode('image', {
        data: {
          content: { url: uploadedUrl, type },
          generated: { url: uploadedUrl, type },
          sourceUrl: uploadedUrl,
          width,
          height,
          size: `${width}x${height}`,
        },
        position: { x: baseX + ((current?.measured?.width ?? 300) + 80), y: baseY },
      });
      try {
        addEdges?.([
          { id: nanoid(), source: nodeId, target: newId, type: 'animated' as const },
        ]);
      } catch { }
      try {
        updateNode(newId, { selected: true });
      } catch { }

      toast.success(t('Frame extracted to new image node'));
    } catch (e) {
      console.error(e);
      toast.error(t('Failed to extract frame'));
    }
  };

  return { extractToImageNode, extractToImageNodeAt };
};


